// FamilyView — Jul 2026 (Randy, Phase A "Family Access Point").
// The ONLY door into an organizer / personal silo for FREE family members
// (accounts in collaborator_ids without their own creator subscription).
// Renders Capture pad + AI Calendar + (kid-only: Rewards + Leaderboard) +
// (parent-only: Pending Confirmations inline) — no creator-dashboard tabs.
//
// Feb 2026 (Randy) role-aware layout:
//   • Parent-role (owner OR parent-pill member):
//       Capture Pad + inline Pending Confirmations + AI Calendar (unlocked,
//       full sub-buttons). NO rewards store, NO leaderboard (they access
//       leaderboard through the AI Calendar sub-buttons).
//   • Kid-role (email collaborator, parent-pill OFF, non-owner):
//       Capture Pad + collapsible Rewards Store + Leaderboard + AI Calendar
//       with kidView (no household setup / rewards / leaderboard sub-buttons).
//
// Feb 2026 (Randy pivot) — "Silo Library" button at the TOP of Family View
// for users who have paid the Creator gate. Unlocked for parent-role,
// LOCKED for kid-role (they get the button visible but with a lock icon).
import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, CalendarHeart, ArrowSquareOut, Books, LockKey, Gift, Trophy, CaretDown, CaretUp, Bell } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, getUser } from "@/lib/api";
import IdentityPill from "@/components/IdentityPill";
import FamilyCapturePad from "@/components/FamilyCapturePad";
import CalendarSection from "@/components/CalendarSection";
import FamilyRewards from "@/components/FamilyRewards";
import FamilyAgenda from "@/components/FamilyAgenda";
import FamilyAsk from "@/components/FamilyAsk";
import PendingConfirmationsModal from "@/components/PendingConfirmationsModal";
import { CalendarThemeProvider, useThemeLabel } from "@/lib/CalendarThemeProvider";
import * as T from "@/lib/themeManifest";

const REWARDS_PREF_KEY = (siloId) => `mysilo_family_rewards_open_${siloId}`;
const LEADERBOARD_PREF_KEY = (siloId) => `mysilo_family_leaderboard_open_${siloId}`;

function readBool(key, fallback) {
  try { const v = localStorage.getItem(key); return v === null ? fallback : v === "1"; }
  catch { return fallback; }
}
function writeBool(key, val) { try { localStorage.setItem(key, val ? "1" : "0"); } catch { /* ignore */ } }

export default function FamilyView() {
  const { siloId } = useParams();
  return (
    <CalendarThemeProvider siloId={siloId}>
      <FamilyViewInner />
    </CalendarThemeProvider>
  );
}

function FamilyViewInner() {
  const { siloId } = useParams();
  const nav = useNavigate();
  const me = getUser();
  const [silo, setSilo] = useState(null);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pendingCount, setPendingCount] = useState(0);
  const [pendingOpen, setPendingOpen] = useState(false);
  // Kid-role collapsible sections
  const [rewardsOpen, setRewardsOpen] = useState(() => readBool(REWARDS_PREF_KEY(siloId), true));
  const [leaderboardOpen, setLeaderboardOpen] = useState(() => readBool(LEADERBOARD_PREF_KEY(siloId), true));
  useEffect(() => { writeBool(REWARDS_PREF_KEY(siloId), rewardsOpen); }, [rewardsOpen, siloId]);
  useEffect(() => { writeBool(LEADERBOARD_PREF_KEY(siloId), leaderboardOpen); }, [leaderboardOpen, siloId]);

  const isPaid = !!me && (me.is_paid_creator || me.platform_subscription_status === "active" || me.role === "super_admin");
  const homePath = isPaid ? "/dashboard" : "/fan-portal";

  // pt89U Stage-C — Re-Themer hooks. Silo-scoped labels; defaults ship
  // as-is if the owner hasn't run the Re-Themer for this silo yet.
  const tTagline           = useThemeLabel(T.THEME_KEY_FAMILYVIEW_TAGLINE);
  const tRoleFamily        = useThemeLabel(T.THEME_KEY_FAMILYVIEW_ROLE_FAMILY);
  const tRoleParent        = useThemeLabel(T.THEME_KEY_HOUSEHOLD_PARENT);
  const tIntro             = useThemeLabel(T.THEME_KEY_FAMILYVIEW_INTRO);
  const tPendingTitle      = useThemeLabel(T.THEME_KEY_FAMILYVIEW_PENDING_TITLE);
  const tPendingSub        = useThemeLabel(T.THEME_KEY_FAMILYVIEW_PENDING_SUB);
  const tPendingCta        = useThemeLabel(T.THEME_KEY_FAMILYVIEW_PENDING_CTA);
  const tRewardStore       = useThemeLabel(T.THEME_KEY_HOUSEHOLD_REWARD_STORE);
  const tLeaderboard       = useThemeLabel(T.THEME_KEY_HOUSEHOLD_LEADERBOARD);
  const tTaskPlural        = useThemeLabel(T.THEME_KEY_CALENDAR_TASK_PLURAL);

  const loadPending = useCallback(async () => {
    try {
      const r = await api.get(`/calendar/${siloId}/pending-confirmations`);
      setPendingCount(r.data?.count || 0);
    } catch { setPendingCount(0); }
  }, [siloId]);

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const r = await api.get(`/silos/${siloId}`);
        if (cancel) return;
        if (!["personal", "organizer"].includes(r.data?.silo_kind)) {
          toast.error("Family view is only for personal / organizer silos.");
          nav(homePath, { replace: true });
          return;
        }
        setSilo(r.data);
        try {
          const s = await api.get(`/calendar/${siloId}/settings`);
          if (!cancel) setSettings(s.data);
        } catch { /* non-fatal */ }
      } catch (e) {
        if (e.response?.status === 401) { nav("/login"); return; }
        toast.error("You don't have access to this family silo.");
        nav(homePath, { replace: true });
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => { cancel = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siloId]);

  useEffect(() => {
    if (!settings?.is_parent_role) return;
    loadPending();
    const t = setInterval(loadPending, 30_000);
    return () => clearInterval(t);
  }, [settings?.is_parent_role, loadPending]);

  if (loading || !silo) {
    return (
      <div className="min-h-screen bg-[#FDFCF8] flex items-center justify-center text-[#6B6A68] text-sm" data-testid="family-view-loading">
        Loading the family organizer…
      </div>
    );
  }

  const inheritedSkus = silo.inherited_skus || [];
  const isParentRole = !!settings?.is_parent_role;
  const isKidRole = !!settings?.is_kid_role;
  // Feb 2026 (Randy pivot) — "Silo Library" button visible only to users
  // who have paid the Creator gate. For parent-role paid creators it's
  // an active link into the traditional silo dashboard. For kid-role paid
  // creators it renders with a lock icon (unclickable). Non-paying
  // collaborators don't see the button at all.
  const showSiloLibrary = isPaid;

  return (
    <div className="min-h-screen bg-[#FDFCF8]" data-testid="family-view-page">
      <header className="border-b hairline bg-white/80 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <Link to={homePath} data-testid="family-view-back" className="p-2 rounded-md text-[#6B6A68] hover:bg-[#F0F1EE]">
              <ArrowLeft size={18} />
            </Link>
            <div className="w-9 h-9 rounded-full bg-[#4A5D4E] text-[#FDFCF8] flex items-center justify-center shrink-0">
              <CalendarHeart size={18} weight="duotone" />
            </div>
            <div className="min-w-0">
              <div className="font-serif-display text-xl leading-none truncate" data-testid="family-view-title">{silo.name}</div>
              <div className="text-[10px] text-[#6B6A68] tracking-[0.25em] uppercase mt-1 flex items-center gap-1.5">
                {tTagline}
                {isParentRole && !settings?.is_owner && (
                  <span className="text-[8px] uppercase tracking-[0.14em] px-1.5 py-0.5 rounded bg-[#2C6E92] text-white border border-[#2C6E92]" data-testid="family-view-parent-tag">
                    {tRoleParent}
                  </span>
                )}
                {isKidRole && (
                  <span className="text-[8px] uppercase tracking-[0.14em] px-1.5 py-0.5 rounded bg-[#F0F1EE] text-[#6B6A68] border hairline" data-testid="family-view-kid-tag">
                    {tRoleFamily}
                  </span>
                )}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {/* Feb 2026 (Randy pivot) — Silo Library button at the top of
                Family View for paid creators. Parent-role → active link
                to the traditional silo dashboard. Kid-role → locked
                icon (they don't need silo library access). */}
            {showSiloLibrary && (
              isKidRole ? (
                <span data-testid="silo-library-locked"
                  title="The silo library is locked for family members. Ask the silo owner if you need access."
                  className="text-xs px-3 py-1.5 rounded-md border hairline bg-[#F0F1EE] text-[#8A8A88] flex items-center gap-1.5 cursor-not-allowed opacity-75">
                  <LockKey size={13} weight="duotone" /> Silo library
                </span>
              ) : (
                <Link to={`/dashboard/silo/${siloId}`} data-testid="silo-library-open"
                  title="Open the full silo library — files, refinery, workbench, silo art"
                  className="text-xs px-3 py-1.5 rounded-md border-2 border-[#1C1B1A] bg-[#1C1B1A] text-[#FDFCF8] hover:bg-[#3A4A3E] hover:border-[#3A4A3E] flex items-center gap-1.5">
                  <Books size={13} weight="duotone" /> Silo library <ArrowSquareOut size={11} />
                </Link>
              )
            )}
            <IdentityPill siloId={silo.id} siloSlug={silo.slug} />
          </div>
        </div>
      </header>

      <main className="siloart-copy max-w-4xl mx-auto px-6 py-8 space-y-8">
        <p className="text-sm text-[#6B6A68] leading-relaxed max-w-2xl" data-testid="family-view-intro">
          {tIntro}
        </p>

        {/* Feb 22, 2026 (Sprint Request 6) — Ask Me Anything moved to the
            TOP of Family View, above the Capture Pad, per Randy's spec. */}
        <FamilyAsk siloSlug={silo.slug} siloId={silo.id} />

        <FamilyCapturePad siloId={silo.id} siloName={silo.name} siloSlug={silo.slug} inheritedSkus={inheritedSkus} />

        {/* Feb 19, 2026 (Randy Photo #2) — "What's happening" surface mounted
            into Family View. Kid-role sees only their own agenda + everyone-
            visible items (no filter dropdown). Parent-role gets a filter
            dropdown: "Just me" / "Everybody" / per-member. */}
        <FamilyAgenda
          siloId={silo.id}
          members={settings?.household?.members || []}
          isParentRole={isParentRole}
        />

        {/* Feb 2026 (Randy) — Parent-role inline pending confirmations.
            Same data as the Fan Portal dot; here it lives inline so the
            parent can approve without leaving Family View. Hidden when
            count === 0 to keep the surface clean. */}
        {isParentRole && pendingCount > 0 && (
          <section className="bg-[#FBEBE4] border border-[#E86A5C]/40 rounded-lg p-4 flex items-start gap-3"
            data-testid="family-view-pending-section">
            <div className="w-8 h-8 rounded-full bg-[#E86A5C] text-white flex items-center justify-center shrink-0">
              <Bell size={14} weight="duotone" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-serif-display text-base text-[#1C1B1A]">
                {pendingCount} {pendingCount === 1 ? tTaskPlural.replace(/s$/i, "") : tTaskPlural} {tPendingTitle}
              </div>
              <div className="text-xs text-[#6B6A68] mt-0.5">
                {tPendingSub}
              </div>
            </div>
            <button onClick={() => setPendingOpen(true)} data-testid="family-view-pending-open"
              className="text-xs px-3 py-1.5 rounded-md bg-[#E86A5C] text-white hover:bg-[#D25B4D] shrink-0">
              {tPendingCta}
            </button>
          </section>
        )}

        {/* Feb 2026 (Randy) — Kid-role top-level Rewards Store (collapsible)
            + Leaderboard. Parents don't see these at the top level; they
            reach the leaderboard/rewards store through the AI Calendar
            sub-buttons at the bottom. */}
        {isKidRole && (
          <section className="bg-white border hairline rounded-lg" data-testid="family-view-rewards-section">
            <button onClick={() => setRewardsOpen((v) => !v)} data-testid="family-view-rewards-toggle"
              className={`w-full text-left flex items-center gap-2 px-4 py-3 ${rewardsOpen ? "border-b hairline" : ""}`}>
              <Gift size={16} weight="duotone" className="text-[#C16E5A]" />
              <span className="font-serif-display text-base flex-1 text-[#1C1B1A]">{tRewardStore}</span>
              {rewardsOpen ? <CaretUp size={12} weight="bold" className="text-[#6B6A68]" /> : <CaretDown size={12} weight="bold" className="text-[#6B6A68]" />}
            </button>
            {rewardsOpen && (
              <div className="p-4">
                <FamilyRewards siloId={silo.id} panel="store" targetMemberId={me?.id} />
              </div>
            )}
          </section>
        )}

        {isKidRole && (
          <section className="bg-white border hairline rounded-lg" data-testid="family-view-leaderboard-section">
            <button onClick={() => setLeaderboardOpen((v) => !v)} data-testid="family-view-leaderboard-toggle"
              className={`w-full text-left flex items-center gap-2 px-4 py-3 ${leaderboardOpen ? "border-b hairline" : ""}`}>
              <Trophy size={16} weight="duotone" className="text-[#C19A4A]" />
              <span className="font-serif-display text-base flex-1 text-[#1C1B1A]">{tLeaderboard}</span>
              {leaderboardOpen ? <CaretUp size={12} weight="bold" className="text-[#6B6A68]" /> : <CaretDown size={12} weight="bold" className="text-[#6B6A68]" />}
            </button>
            {leaderboardOpen && (
              <div className="p-4">
                <FamilyRewards siloId={silo.id} panel="leaderboard" />
              </div>
            )}
          </section>
        )}

        {/* AI Calendar — kidView on for kid-role so the sub-button stack
            (Household Setup / Reward store / Leaderboard / Digest) is
            hidden. Parents get the full stack. Both get the calendar grid
            with their assigned chores. */}
        <CalendarSection
          siloId={silo.id}
          siloName={silo.name}
          siloSlug={silo.slug}
          inheritedSkus={inheritedSkus}
          kidView={isKidRole}
        />
      </main>

      {pendingOpen && (
        <PendingConfirmationsModal
          siloId={silo.id} siloName={silo.name}
          onClose={() => setPendingOpen(false)}
          onCountChanged={(n) => setPendingCount(n)}
        />
      )}
    </div>
  );
}
